// src/pages/ReviewWorkbenchPage.js
// Workbench único para revisão humana de propostas AI e triage de capturas.
// Tabs:
//   - Propostas AI    → ai_proposals (review_status='pending', exceto entity_create) → ProposalReviewList
//   - Empresas EREL   → ai_proposals (proposal_type='entity_create', pending)  → ErelEntityReviewList (G3B)
//   - Triage WhatsApp → mensagens_whatsapp (intake_status='pending_triage') → TriageConversationList (G-UX1)
//   - Triage Chamadas → chamadas (intake_status='pending_triage')     → TriageCallList
//
// entity_create SAI da tab genérica: a decisão dessas propostas é exclusiva do
// RPC fn_erel_review_proposal (G3B_SPEC §2.1) — o update direto da tab genérica
// furaria o gate (sem learning_signal, sem apply, sem roteamento de erel_jobs).

import React, { useEffect, useState } from 'react';
import { ClipboardCheck, Sparkles, Building2, MessageCircle, Phone, FileSpreadsheet, RefreshCw } from 'lucide-react';
import AppHeader from '../components/layout/AppHeader';
import { supabase } from '../supabaseClient';
import ProposalReviewList from '../components/review/ProposalReviewList';
import ErelEntityReviewList from '../components/review/ErelEntityReviewList';
import TriageConversationList from '../components/review/TriageConversationList';
import TriageCallList from '../components/review/TriageCallList';
import IntakeDecisionCardList from '../components/review/IntakeDecisionCardList';

// G-UX1: orientação contextual por tab — Entrada (triage) pergunta "negócio ou
// ruído?"; Saída (revisão de IA) pergunta "a IA entendeu certo? pode registrar?"
const TAB_GUIDANCE = {
  whatsapp: {
    flow: 'Entrada',
    question: 'Isto é negócio ou ruído?',
    effect: 'Negócio/Ignorar define o intake_status de todas as mensagens pendentes da conversa (ou só das selecionadas).',
  },
  calls: {
    flow: 'Entrada',
    question: 'Isto é negócio ou ruído?',
    effect: 'Negócio/Ignorar define o intake_status da chamada.',
  },
  proposals: {
    flow: 'Saída',
    question: 'A IA entendeu certo?',
    effect: 'Aprovar/Rejeitar apenas muda o status da proposta — o apply governado de fatos chega no G5.',
  },
  erel: {
    flow: 'Saída',
    question: 'A IA entendeu certo? Pode registrar?',
    effect: 'Cada decisão grava o learning_signal; Aprovar aplica a empresa (create/link) na mesma transação.',
  },
  precos: {
    flow: 'Saída',
    question: 'O que aplicar desta tabela de preço?',
    effect: 'Visualização read-only do decision card (Grill Me v1); a aplicação governada de estrutura/preço chega na Fase III-B.',
  },
};

export default function ReviewWorkbenchPage() {
  const [activeTab, setActiveTab] = useState('proposals');
  const [counts, setCounts] = useState({ proposals: null, erel: null, whatsapp: null, calls: null, precos: null });
  const [loadingCounts, setLoadingCounts] = useState(true);

  const loadCounts = async () => {
    setLoadingCounts(true);
    const [p, e, w, c] = await Promise.all([
      supabase
        .from('ai_proposals')
        .select('*', { count: 'exact', head: true })
        .eq('review_status', 'pending')
        .neq('proposal_type', 'entity_create'),
      supabase
        .from('ai_proposals')
        .select('*', { count: 'exact', head: true })
        .eq('review_status', 'pending')
        .eq('proposal_type', 'entity_create'),
      supabase
        .from('mensagens_whatsapp')
        .select('*', { count: 'exact', head: true })
        .eq('intake_status', 'pending_triage'),
      supabase
        .from('chamadas')
        .select('*', { count: 'exact', head: true })
        .eq('intake_status', 'pending_triage'),
    ]);
    setCounts((prev) => ({
      ...prev,
      proposals: p.count ?? 0,
      erel: e.count ?? 0,
      whatsapp: w.count ?? 0,
      calls: c.count ?? 0,
    }));
    setLoadingCounts(false);
  };

  useEffect(() => {
    let mounted = true;
    (async () => {
      await loadCounts();
      if (!mounted) return;
    })();
    return () => { mounted = false; };
  }, []);

  return (
    <div className="min-h-screen bg-slate-50">
      <AppHeader
        title="Review Workbench"
        subtitle="Revisão de propostas AI e triage de capturas"
        icon={ClipboardCheck}
      />
      <main className="w-full px-4 lg:px-6 2xl:px-8 py-4 lg:py-6 space-y-3 max-w-6xl mx-auto">
        <div className="flex items-center justify-between border-b border-slate-200 mb-4">
          <div className="flex">
            <TabButton
              active={activeTab === 'proposals'}
              onClick={() => setActiveTab('proposals')}
              label="Propostas AI"
              icon={Sparkles}
              count={counts.proposals}
            />
            <TabButton
              active={activeTab === 'erel'}
              onClick={() => setActiveTab('erel')}
              label="Empresas EREL"
              icon={Building2}
              count={counts.erel}
            />
            <TabButton
              active={activeTab === 'whatsapp'}
              onClick={() => setActiveTab('whatsapp')}
              label="Triage WhatsApp"
              icon={MessageCircle}
              count={counts.whatsapp}
            />
            <TabButton
              active={activeTab === 'calls'}
              onClick={() => setActiveTab('calls')}
              label="Triage Chamadas"
              icon={Phone}
              count={counts.calls}
            />
            <TabButton
              active={activeTab === 'precos'}
              onClick={() => setActiveTab('precos')}
              label="Tabelas de Preço"
              icon={FileSpreadsheet}
              count={counts.precos}
            />
          </div>
          <button
            onClick={loadCounts}
            disabled={loadingCounts}
            className="px-2 py-1 text-[11px] text-slate-600 hover:bg-slate-100 rounded flex items-center gap-1 disabled:opacity-50"
            title="Atualizar contagens"
          >
            <RefreshCw size={11} className={loadingCounts ? 'animate-spin' : ''} />
          </button>
        </div>

        <GuidanceStrip guidance={TAB_GUIDANCE[activeTab]} />

        {activeTab === 'proposals' && (
          <ProposalReviewList onChange={loadCounts} />
        )}
        {activeTab === 'erel' && (
          <ErelEntityReviewList onChange={loadCounts} />
        )}
        {activeTab === 'whatsapp' && (
          <TriageConversationList
            onChange={loadCounts}
            onLoadedCount={(n) => setCounts((c) => ({ ...c, whatsapp: n }))}
          />
        )}
        {activeTab === 'calls' && (
          <TriageCallList onChange={loadCounts} />
        )}
        {activeTab === 'precos' && (
          <IntakeDecisionCardList
            onLoadedCount={(n) => setCounts((c) => ({ ...c, precos: n }))}
          />
        )}
      </main>
    </div>
  );
}

function GuidanceStrip({ guidance }) {
  if (!guidance) return null;
  return (
    <div className="flex items-center gap-2 flex-wrap px-3 py-1.5 bg-slate-100 border border-slate-200 rounded text-[11px] text-slate-600">
      <span
        className={`px-1.5 py-0 rounded text-[10px] font-semibold uppercase tracking-wide ${
          guidance.flow === 'Entrada'
            ? 'bg-blue-100 text-blue-700'
            : 'bg-violet-100 text-violet-700'
        }`}
      >
        {guidance.flow}
      </span>
      <span className="font-medium text-slate-800">{guidance.question}</span>
      <span className="text-slate-400">·</span>
      <span>{guidance.effect}</span>
    </div>
  );
}

function TabButton({ active, onClick, label, icon: Icon, count }) {
  return (
    <button
      onClick={onClick}
      className={`px-3 py-2 -mb-px border-b-2 text-xs font-medium transition-colors flex items-center gap-1.5 ${
        active
          ? 'border-blue-600 text-blue-700'
          : 'border-transparent text-slate-600 hover:text-slate-900'
      }`}
    >
      {Icon && <Icon size={13} />} {label}
      {count != null && count > 0 && (
        <span
          className={`ml-1 text-[10px] font-semibold rounded-full px-1.5 py-0 min-w-[16px] text-center leading-4 ${
            active ? 'bg-blue-600 text-white' : 'bg-red-600 text-white'
          }`}
        >
          {count}
        </span>
      )}
    </button>
  );
}

